import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail, Globe2, Calendar, Phone, Shield, Save, Loader2 } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import StatusBadge from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/button";

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setDetail(data);
      setForm({ name: data.user.name || "", phone: data.user.phone || "", country: data.user.country || "", role: data.user.role });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${userId}`, form);
      toast.success("User updated");
      setEditing(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <AdminGuard>
      <AdminLayout>
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[hsl(var(--zt-teal))]" size={28} /></div>
      </AdminLayout>
    </AdminGuard>
  );

  if (!detail) return (
    <AdminGuard>
      <AdminLayout>
        <button onClick={() => navigate("/admin/users")} className="flex items-center gap-2 text-sm text-[hsl(var(--zt-charcoal))]/60 hover:text-[hsl(var(--zt-teal))] mb-6 font-heading uppercase tracking-wider transition-colors">
          <ArrowLeft size={15} /> Back to Users
        </button>
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-16 text-center shadow-sm">
          <p className="text-sm text-[hsl(var(--zt-charcoal))]/40">User not found</p>
        </div>
      </AdminLayout>
    </AdminGuard>
  );

  const u = detail.user;

  return (
    <AdminGuard>
      <AdminLayout>
        <button onClick={() => navigate("/admin/users")} className="flex items-center gap-2 text-sm text-[hsl(var(--zt-charcoal))]/60 hover:text-[hsl(var(--zt-teal))] mb-6 font-heading uppercase tracking-wider transition-colors">
          <ArrowLeft size={15} /> Back to Users
        </button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Profile Card */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm">
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-2xl bg-[hsl(var(--zt-teal))] grid place-items-center text-white text-xl font-bold shadow-sm flex-shrink-0">
                  {(u?.name || u?.email || "U").charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="font-display text-lg text-[hsl(var(--zt-teal))] font-bold">{u?.name || "No name"}</h2>
                  <StatusBadge status={u?.role} />
                </div>
              </div>

              <div className="space-y-3 text-sm">
                <InfoRow icon={Mail} label="Email" value={u?.email} />
                <InfoRow icon={Phone} label="Phone" value={u?.phone || "—"} />
                <InfoRow icon={Globe2} label="Country" value={u?.country || "—"} />
                <InfoRow icon={Shield} label="Provider" value={u?.auth_provider} />
                <InfoRow icon={Calendar} label="Joined" value={fmtDate(u?.created_at)} />
              </div>

              <div className="mt-5 pt-5 border-t border-[hsl(var(--zt-beige-deep))] flex gap-2">
                <StatusBadge status={u?.is_active ? "active" : "inactive"} />
                {u?.is_deleted && <StatusBadge status="deleted" />}
              </div>
            </div>

            {/* Edit form */}
            {editing ? (
              <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 shadow-sm space-y-4">
                <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">Edit Profile</h3>
                {[
                  { key: "name", label: "Name", type: "text" },
                  { key: "phone", label: "Phone", type: "text" },
                  { key: "country", label: "Country", type: "text" },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1">{f.label}</label>
                    <input
                      type={f.type}
                      value={form[f.key] || ""}
                      onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full h-9 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-lg focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1">Role</label>
                  <select
                    value={form.role || "user"}
                    onChange={e => setForm(p => ({ ...p, role: e.target.value }))}
                    className="w-full h-9 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-lg focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30"
                  >
                    <option value="user">User</option>
                    <option value="advisor">Advisor</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button onClick={save} disabled={saving} size="sm" className="bg-[hsl(var(--zt-teal))] text-white rounded-lg h-9 text-xs font-heading uppercase tracking-wider flex-1">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <><Save size={13} className="mr-1.5" />Save</>}
                  </Button>
                  <Button onClick={() => setEditing(false)} variant="outline" size="sm" className="rounded-lg h-9 text-xs font-heading uppercase tracking-wider">Cancel</Button>
                </div>
              </div>
            ) : (
              <Button onClick={() => setEditing(true)} className="w-full bg-[hsl(var(--zt-teal))] text-white rounded-xl h-10 font-heading uppercase tracking-wider text-xs">
                Edit Profile
              </Button>
            )}
          </div>

          {/* History sections */}
          <div className="lg:col-span-2 space-y-6">
            <HistorySection title="Consultations" items={detail?.consultations} renderItem={c => (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[hsl(var(--zt-teal))]">{c.subject}</p>
                  <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{c.advisor_name || "Unassigned"} · {fmtDate(c.created_at)}</p>
                </div>
                <StatusBadge status={c.status} />
              </div>
            )} />

            <HistorySection title="Appointments" items={detail?.appointments} renderItem={a => (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[hsl(var(--zt-teal))]">{a.advisor_name || "—"}</p>
                  <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{fmtDateTime(a.scheduled_at)} · {a.meeting_mode}</p>
                </div>
                <StatusBadge status={a.status} />
              </div>
            )} />

            <HistorySection title="Consultation Requests (Leads)" items={detail?.leads} renderItem={l => (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-[hsl(var(--zt-teal))]">{l.interest}</p>
                  <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55">{fmtDate(l.created_at)}</p>
                </div>
                <StatusBadge status={l.status} />
              </div>
            )} />
          </div>
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon size={14} className="text-[hsl(var(--zt-gold))] flex-shrink-0" />
      <span className="text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/50 w-20">{label}</span>
      <span className="text-sm font-semibold text-[hsl(var(--zt-charcoal))]/80 truncate">{value}</span>
    </div>
  );
}

function HistorySection({ title, items = [], renderItem }) {
  return (
    <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] shadow-sm overflow-hidden">
      <div className="px-6 py-4 border-b border-[hsl(var(--zt-beige-deep))] bg-[hsl(var(--zt-beige))]/40">
        <h3 className="font-heading uppercase tracking-[0.18em] text-[11px] text-[hsl(var(--zt-teal))]/70 font-bold">
          {title} <span className="text-[hsl(var(--zt-charcoal))]/40 font-normal ml-1">({items.length})</span>
        </h3>
      </div>
      {items.length === 0 ? (
        <p className="px-6 py-8 text-sm text-center text-[hsl(var(--zt-charcoal))]/40">No records</p>
      ) : (
        <ul className="divide-y divide-[hsl(var(--zt-beige-deep))]/60">
          {items.map((item, i) => (
            <li key={i} className="px-6 py-4 hover:bg-[hsl(var(--zt-beige))]/30 transition-colors">
              {renderItem(item)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
